import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Box, Typography, Grid2, Divider, Checkbox, Tooltip } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { formatDateRange } from "../../../Helper/Analytics/AnalyticsHelper";
import RenderChartLegend from "../../AdvancedAnalytics/ContinuousChart/RenderChartLegend";
import BreakdownChart from "../../BmsFaults/Analytics/BreakdownChart";
import BMSFaultChart from "../../BmsFaults/Components/StyledComponents/BMSFaultChart";
import SearchInput from "../../Overview/components/SearchInput";
import {
  GroupByButton,
  DropdownMenu,
  GroupByMenuItem,
} from "../../BmsFaults/Components/StyledComponents";
import { getSmartAlertsList } from "../../../Api/SmartAlertsAnalyticsApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { SMART_ALERTS_GROUP_BY_OPTIONS } from "./breakdownOptions";
import { resolveBreakdownDimensions } from "../../../Helper/Analytics/breakdownDimensions";
import { useOrgLabels, usePagesContent } from "../../../Hooks/usePagesContent";


const buildChartConfig = (chartData, handleDataPointSelection) => {
  if (!chartData || chartData.length === 0) return { series: [], options: {} };

  const categories  = chartData.map((item) => item.alertName);
  const rawWarning  = chartData.map((item) => item.warning  || 0);
  const rawHigh     = chartData.map((item) => item.high     || 0);
  const rawCritical = chartData.map((item) => item.critical || 0);
  const rawTotals   = rawWarning.map((w, i) => w + rawHigh[i] + rawCritical[i]);

  const maxValue     = Math.max(...rawTotals);
  const NO_VALUE_MIN = maxValue > 0 ? maxValue * 0.02 : 1;
  const MIN_WIDTH    = maxValue > 0 ? maxValue * 0.03 : 1;

  const toVisual = (val, i) => {
    const total = rawTotals[i];
    if (total === 0) return NO_VALUE_MIN / 3;
    const visualTotal = Math.max(total, MIN_WIDTH);
    return (val / total) * visualTotal;
  };

  const visualWarning  = rawWarning.map(toVisual);
  const visualHigh     = rawHigh.map(toVisual);
  const visualCritical = rawCritical.map(toVisual);
  const visualTotals   = visualWarning.map((w, i) => w + visualHigh[i] + visualCritical[i]);
  const xAxisMax       = Math.max(...visualTotals) * 1.1;

  const series = [
    { name: "Warning",       data: visualWarning  },
    { name: "High Severity", data: visualHigh     },
    { name: "Critical",      data: visualCritical },
  ];

  const options = {
    chart: {
      id: "triggered-alert-breakdown-chart",
      stacked: true,
      events: { dataPointSelection: handleDataPointSelection },
    },
    plotOptions: {
      bar: {
        dataLabels: {
          total: {
            enabled: true,
            offsetX: 8,
            offsetY: 0,
            formatter: (value, opts) => rawTotals[opts.dataPointIndex],
            style: { color: "#494949", fontSize: "12px", fontFamily: "Roboto", fontWeight: 400, lineHeight: "16px" },
          },
        },
      },
    },
    colors: ["#FFBF3D", "#FF7A3D", "#E03939"],
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      max: xAxisMax,
      labels:     { show: false },
      axisTicks:  { show: false },
      axisBorder: { show: false },
    },
    yaxis: { show: false },
    legend: { show: false },
    tooltip: {
      shared: true,
      intersect: false,
      x: { formatter: (val, opts) => categories[opts?.dataPointIndex] || val },
      y: {
        formatter: (val, opts) => {
          const i = opts.dataPointIndex;
          const s = opts.seriesIndex;
          const raw = [rawWarning, rawHigh, rawCritical][s];
          return (raw?.[i] ?? val) + " alerts";
        },
      },
    },
    grid: { show: false, padding: { left: 16, right: 0, top: -10, bottom: 24 } },
    states: { active: { filter: { type: "none" } } },
  };

  return { series, options };
};

const LEGEND_SERIES = [
  { name: "Warning",       color: "#FFBF3D" },
  { name: "High Severity", color: "#FF7A3D" },
  { name: "Critical",      color: "#E03939" },
];

const ALL_ITEM_ID = "all";

const SIDEBAR_LIST_MAX_HEIGHT = "350px";

const TriggeredAlertBreakdownChartView = ({
  title,
  height = "582px",
  startDate,
  endDate,
  globalFilters = {},
  onDataPointClick,
}) => {
  const pagesContent = usePagesContent();
  const { fleetLabel, fleetCategoryLabel } = useOrgLabels();

  // The menu, from PlatformAlertsV2.breakdownDimensions where the org configures
  // it. The two customer dimensions take their names from the same org meta row
  // that names them in the filter and the columns.
  const groupByOptions = useMemo(
    () =>
      resolveBreakdownDimensions(
        pagesContent?.metaInfo?.breakdownDimensions,
        SMART_ALERTS_GROUP_BY_OPTIONS,
        { fleetName: `By ${fleetLabel}`, fleetCategory: `By ${fleetCategoryLabel}` }
      ),
    [pagesContent, fleetLabel, fleetCategoryLabel]
  );

  const [groupBy, setGroupBy]               = useState("originCity");
  const [selectedOption, setSelectedOption] = useState(SMART_ALERTS_GROUP_BY_OPTIONS[0]);
  const [selectedItems, setSelectedItems]   = useState([]);
  const [searchQuery, setSearchQuery]       = useState("");
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [loading, setLoading]               = useState(false);
  const [chartData, setChartData]           = useState([]);
  const [sidebarItems, setSidebarItems]     = useState([]);

  const selectedItemsRef   = useRef([]);
  const sidebarItemsRef    = useRef([]);
  const chartDataRef       = useRef(chartData);
  const onClickRef         = useRef(onDataPointClick);
  const handleItemTimerRef = useRef(null);

  useEffect(() => { chartDataRef.current = chartData; }, [chartData]);
  useEffect(() => { onClickRef.current = onDataPointClick; }, [onDataPointClick]);

  const mapChartRows = (rows) =>
    rows.map((row) => {
      const metrics = row.metricsArray || [];
      const getCount = (type) => metrics.find((m) => m.type === type)?.value ?? 0;
      return {
        alertName: row.xLabel,
        warning:  getCount("Warning") || getCount("Medium") || getCount("Low") || 0,
        high:     getCount("High Severity") || getCount("High"),
        critical: getCount("Critical"),
      };
    });

  const fetchChartData = useCallback((selectedItemsArr, currentGroupBy, currentStartDate, currentEndDate) => {
    const isAllSelected = selectedItemsArr.some((i) => i.id === ALL_ITEM_ID);
    let filters;
    if (isAllSelected || selectedItemsArr.length === 0) {
      const allIds = sidebarItemsRef.current
        .filter((i) => i.id !== ALL_ITEM_ID)
        .map((i) => i.id);
      filters = allIds.length > 0 ? [{ name: currentGroupBy, values: allIds }] : [];
    } else {
      filters = [{ name: currentGroupBy, values: selectedItemsArr.map((i) => i.id) }];
    }

    return getSmartAlertsList(globalFilters, {
      startDate: currentStartDate,
      endDate:   currentEndDate,
      select:    ["GeneratedAlertsCount"],
      groupBy:   ["alertDescription", "AlertSeverityName"],
      ...(filters.length ? { filters } : {}),
    });
  }, [globalFilters]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);

    getSmartAlertsList(globalFilters, {
      startDate,
      endDate,
      select: ["GeneratedAlertsCount"],
      groupBy: [groupBy],
    }).then((sidebarRes) => {
      if (cancelled) return;

      const rows = sidebarRes?.responseStatus?.code === CODE.SUCCESS
        ? sidebarRes.response?.data || []
        : [];

      const items = rows.map((row) => ({
        id: row.xLabel,
        label: row.xLabel,
        count: row.metricsArray?.find((m) => m.type === "GeneratedAlertsCount")?.value ?? 0,
      }));

      const totalCount = items.reduce((sum, i) => sum + i.count, 0);
      const allItems = [{ id: ALL_ITEM_ID, label: "All", count: totalCount }, ...items];

      setSidebarItems(allItems);
      sidebarItemsRef.current = allItems;
      const defaultItem = allItems[0];
      setSelectedItems([defaultItem]);
      selectedItemsRef.current = [defaultItem];
      setSearchQuery("");

      return fetchChartData([defaultItem], groupBy, startDate, endDate);
    }).then((chartRes) => {
      if (cancelled || !chartRes) return;

      const rows = chartRes?.responseStatus?.code === CODE.SUCCESS
        ? chartRes.response?.data || []
        : [];

      setChartData(mapChartRows(rows));
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [groupBy, startDate, endDate, globalFilters, fetchChartData]);

  const handleItemToggle = useCallback((item) => {
    if (loading) return;

    const prev = selectedItemsRef.current;
    const isAllSelected = prev.some((i) => i.id === ALL_ITEM_ID);
    let next;
    if (item.id === ALL_ITEM_ID) {
      next = isAllSelected ? [] : [item];
    } else {
      if (isAllSelected) {
        next = sidebarItemsRef.current.filter((i) => i.id !== ALL_ITEM_ID && i.id !== item.id);
      } else {
        next = prev.some((i) => i.id === item.id)
          ? prev.filter((i) => i.id !== item.id)
          : [...prev, item];
      }
    }
    const allIndividual = sidebarItemsRef.current.filter((i) => i.id !== ALL_ITEM_ID);
    if (next.length === allIndividual.length && !next.some((i) => i.id === ALL_ITEM_ID)) {
      const allItem = sidebarItemsRef.current.find((i) => i.id === ALL_ITEM_ID);
      if (allItem) next = [allItem];
    }

    selectedItemsRef.current = next;
    setSelectedItems(next);

    if (next.length === 0) {
      setChartData([]);
      return;
    }

    setLoading(true);
    clearTimeout(handleItemTimerRef.current);
    handleItemTimerRef.current = setTimeout(() => {
      fetchChartData(next, groupBy, startDate, endDate).then((chartRes) => {
        const rows = chartRes?.responseStatus?.code === CODE.SUCCESS
          ? chartRes.response?.data || []
          : [];
        setChartData(mapChartRows(rows));
        setLoading(false);
      }).catch(() => setLoading(false));
    }, 150);
  }, [loading, groupBy, startDate, endDate, fetchChartData]);

  const handleDataPointSelection = useCallback((event, chartContext, config) => {
    const curData     = chartDataRef.current;
    const cats        = curData.map((d) => d.alertName);
    const rawWarning  = curData.map((d) => d.warning  || 0);
    const rawHigh     = curData.map((d) => d.high     || 0);
    const rawCritical = curData.map((d) => d.critical || 0);
    const seriesNames = ["Warning", "High Severity", "Critical"];
    const di          = config.dataPointIndex;
    const si          = config.seriesIndex;

    const clickedData = {
      faultName:      cats[di] || "",
      value:          [rawWarning, rawHigh, rawCritical][si]?.[di] || 0,
      dataPointIndex: di,
      seriesIndex:    si,
      selectedItems:  selectedItemsRef.current,
      groupBy:        selectedOption,
      severityName:   seriesNames[si] || "",
    };

    if (onClickRef.current) onClickRef.current(clickedData, selectedOption.value);
  }, [selectedOption]);

  const filteredItems = sidebarItems.filter((item) =>
    item.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (!groupByOptions.length) return;
    if (groupByOptions.some((option) => option.value === groupBy)) return;
    setGroupBy(groupByOptions[0].value);
    setSelectedOption(groupByOptions[0]);
  }, [groupByOptions, groupBy]);

  const chartConfig = useMemo(
    () => buildChartConfig(chartData, handleDataPointSelection),
    [chartData, handleDataPointSelection]
  );

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
        overflow: "hidden",
        height: height,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 22px",
        }}
      >
        <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
          {title}
        </Typography>
        <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
          {formatDateRange(startDate, endDate)}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ display: "flex", height: `calc(${height} - 72px)`, overflow: "hidden" }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: "320px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            mb: 2,
            padding: "0px 0px 30px 0px",
          }}
        >
          <Box sx={{ padding: "0 16px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Tooltip
                title={selectedOption?.shortLabel ? selectedOption.label : ""}
                placement="top"
                arrow
              >
                <GroupByButton
                  onClick={(e) => setDropdownAnchorEl(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon sx={{ color: "#5E758D", fontSize: "10px" }} />}
                  startIcon={<GridViewRoundedIcon sx={{ color: "#5E758D", fontSize: "12px" }} />}
                >
                  {selectedOption.label}
                </GroupByButton>
              </Tooltip>
              <Typography variant="fw400s12c686868">ISSUES</Typography>
            </Box>

            <Divider sx={{ mb: 1, borderColor: "#E4E4E4" }} />

            <Box sx={{ mb: 1 }}>
              <SearchInput
                placeholder={`Search ${selectedOption.label.replace("By ", "") || "City"}`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            <DropdownMenu
              anchorEl={dropdownAnchorEl}
              open={Boolean(dropdownAnchorEl)}
              onClose={() => setDropdownAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              {groupByOptions.map((option) => (
                <GroupByMenuItem
                  key={option.value}
                  selected={groupBy === option.value}
                  onClick={() => {
                    setGroupBy(option.value);
                    setSelectedOption(option);
                    setSearchQuery("");
                    setDropdownAnchorEl(null);
                  }}
                >
                  {option.label}
                </GroupByMenuItem>
              ))}
            </DropdownMenu>
          </Box>

          <Box sx={{ maxHeight: SIDEBAR_LIST_MAX_HEIGHT, overflowY: "auto", padding: "0px 20px 0px 28px" }}>
            {loading ? (
              <Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
                <CartLoader />
              </Box>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ padding: "16px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">No items found</Typography>
              </Box>
            ) : (
              <Grid2 container gap={0.5} flexDirection="column">
                {filteredItems.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => handleItemToggle(item)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      cursor: "pointer",
                      "&:hover": { opacity: 0.8 },
                      transition: "opacity 0.2s",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        checked={
                          selectedItems.some((i) => i.id === ALL_ITEM_ID) ||
                          selectedItems.some((i) => i.id === item.id)
                        }
                        indeterminate={
                          item.id === ALL_ITEM_ID &&
                          !selectedItems.some((i) => i.id === ALL_ITEM_ID) &&
                          selectedItems.length > 0
                        }
                        sx={{
                          padding: 0,
                          color: "#6D72F6",
                          "&.Mui-checked":               { color: "#6D72F6" },
                          "&.MuiCheckbox-indeterminate": { color: "#6D72F6" },
                          "& .MuiSvgIcon-root":          { fontSize: "20px" },
                        }}
                      />
                      <Typography
                        variant="fw400s14c000000"
                        sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                      >
                        {item.label || "Not Set"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="fw600s14c000000"
                      sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </Grid2>
            )}
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", mb: 6 }} />

        {/* Right — Chart */}
        <Box
          sx={{
            flex: 1,
            padding: "24px 28px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="fw600s12c8D8D8D" sx={{ textTransform: "uppercase", color: "#8F8F8F" }}>
                Alert Name
              </Typography>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <RenderChartLegend
                  legendSeries={LEGEND_SERIES}
                  chartIDs={["triggered-alert-breakdown-chart"]}
                  isLoading={loading}
                  centered={true}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            {loading ? (
              <Box
                sx={{
                  padding: "32px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                }}
              >
                <CartLoader />
              </Box>
            ) : selectedItems.length === 0 ? (
              <Box sx={{ padding: "32px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">Select at least one item</Typography>
              </Box>
            ) : chartData.length === 0 ? (
              <Box sx={{ padding: "32px", textAlign: "center" }}>
                <Typography variant="fw400s14c686868">No chart data available</Typography>
              </Box>
            ) : (
              <BMSFaultChart drillDownEnabled={true}>
                <BreakdownChart
                  series={chartConfig.series}
                  categories={chartConfig.options?.xaxis?.categories || []}
                  options={chartConfig.options}
                  minVisibilityHeight={400}
                />
              </BMSFaultChart>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TriggeredAlertBreakdownChartView;
